import { Hub } from "@sentry/browser";
import { ILogService } from "angular";
import { $InjectorLike } from "angular-ui-router";
import { getExceptionMessage, dropStackFrames } from "../data-utils";
import { notifySentryHubError } from "./sentry-exception-handler.decorator";

const pristineConsole = {
    debug: console.debug.bind(console),
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
};

class HandledError extends Error {
    // Manually provide the name for minification safety
    name = "HandledError";
}

export function sentryLogDecorator($delegate: ILogService, $injector: $InjectorLike) {
    'ngInject';
    const methodMap: ["debug", "log", "warn", "error"] = ["debug", "log", "warn", "error"];
    const originals = methodMap.reduce((originals, method) => {
        originals[method] = $delegate[method];
        return originals;
    }, {});
    for (const method of methodMap) {
        switch (method) {
            case "error":
                $delegate[method] = (...messages) => notifySentryIfException(method, ...messages);
                break;
            default:
                $delegate[method] = (...messages) => originals[method].apply($delegate, messages);

        }
    }
    return $delegate;

    function notifySentryIfException(method: string, ...messages: any[]) {
        // Compute a stack trace from the current frame. Drop the current frame (local)
        // and the immediate parent (anonymous function) since they will never be relevant.
        const stack = dropStackFrames(new Error().stack, 2);
        let shouldCallDelegate = true;
        const hub = $injector.get<Hub>("sentryLogHub");
        if (hub !== null) {
            shouldCallDelegate = false;
            if (messages && messages[0] && messages[0].stack) {
                // This is an Error, so include extra details.
                // This branch is followed if a caught exception calls `$log.error(ex)`,
                // which won't go through the exception handler, but we probably still want to know about.
                const [exception, ...extra] = messages as [Error, ...any[]];
                // Include the current trace in the exception trace to make it easier to find
                // where exceptions were logged.
                exception.stack += "\n" + stack;
                notifySentryHubError(hub, exception, null, true, ...extra);
            } else {
                const message = getExceptionMessage(messages[0]);
                // Build an error from the first argument so that this produces a stack trace.
                const exception = new HandledError(message);
                notifySentryHubError(hub, exception, null, true, ...messages);
            }
        }
        if (shouldCallDelegate) {
            // If the message is not handled by Sentry, continue through the AngularJS $log service
            originals[method].apply($delegate, messages);
        } else {
            // Call a pristine log method so that the message is logged to console, but not appended to Sentry.
            pristineConsole[method].apply($delegate, messages);
        }
    }
}
